/**
 * Lo que manda un formulario HTML llega **plano**: cada control es un nombre y un valor. Los
 * campos que guardan más de una cosa se nombran con corchetes, como se ha hecho siempre en la
 * web:
 *
 * - `extras[]` — casillas: una lista (`extras: ['a', 'b']`);
 * - `zona[pais]`, `zona[codigo]` — un lugar: un objeto (`zona: { pais, codigo }`);
 * - `lineas[0][concepto]` — un repetidor: una lista de filas.
 *
 * Esto los despliega en lo que entiende `validarEnvio`. Sin ello, las casillas y el repetidor
 * de un formulario enviado desde el tema —con JavaScript o sin él, que van los dos como
 * `multipart/form-data`— se descartaban por no estar declarados. Lo que no tiene una de esas
 * tres formas se deja tal cual, y `validarEnvio` descarta lo que no declara el formulario.
 *
 * Un nombre lo escribe quien envía: `__proto__`, `constructor` y `prototype` no se admiten en
 * ningún tramo, ni más de dos niveles, ni más de `MAXIMO_DE_FILAS` filas.
 */
const PROHIBIDOS = new Set(['__proto__', 'constructor', 'prototype'])

/** Más filas que las que admite cualquier repetidor (50): lo de más allá ni se mira. */
export const MAXIMO_DE_FILAS = 50

const CON_CORCHETES = /^([^[\]]+)((?:\[[^[\]]*\]){1,2})$/

const esObjeto = (v: unknown): v is Record<string, unknown> => Boolean(v) && typeof v === 'object' && !Array.isArray(v)

export const desplegarCorchetes = (entrada: Record<string, unknown>): Record<string, unknown> => {
  const salida: Record<string, unknown> = {}
  const anidadas: Array<[string, string[], unknown]> = []
  for (const [nombre, valor] of Object.entries(entrada)) {
    const partes = CON_CORCHETES.exec(nombre)
    if (!partes) {
      // Un nombre con corchetes que no es de las tres formas no es de ningún campo.
      if (!PROHIBIDOS.has(nombre) && !/[[\]]/.test(nombre)) salida[nombre] = valor
      continue
    }
    const tramos = [...partes[2]!.matchAll(/\[([^[\]]*)\]/g)].map((t) => t[1]!)
    if (PROHIBIDOS.has(partes[1]!) || tramos.some((t) => PROHIBIDOS.has(t))) continue
    anidadas.push([partes[1]!, tramos, valor])
  }

  // Las filas de un repetidor se juntan por su número, y después se compactan: un hueco
  // (la fila 0 borrada, la 1 enviada) no es una fila vacía.
  const filas = new Map<string, Map<number, Record<string, unknown>>>()
  for (const [raiz, tramos, valor] of anidadas) {
    const [primero, segundo] = tramos as [string, string | undefined]
    if (segundo === undefined) {
      if (primero === '') {
        // `extras[]`: una lista. Si el nombre se repite, el cuerpo ya lo trae como lista.
        const antes = Array.isArray(salida[raiz]) ? (salida[raiz] as unknown[]) : []
        salida[raiz] = [...antes, ...(Array.isArray(valor) ? valor : [valor])]
      } else if (/^\d+$/.test(primero)) {
        // `extras[0]`: una lista por posiciones.
        const n = Number(primero)
        if (n >= MAXIMO_DE_FILAS) continue
        const antes = Array.isArray(salida[raiz]) ? (salida[raiz] as unknown[]) : []
        antes[n] = valor
        salida[raiz] = antes
      } else {
        // `zona[pais]`: un objeto. Un nombre repetido se queda con el último valor.
        const antes = esObjeto(salida[raiz]) ? (salida[raiz] as Record<string, unknown>) : {}
        antes[primero] = Array.isArray(valor) ? valor.at(-1) : valor
        salida[raiz] = antes
      }
      continue
    }
    // `lineas[0][concepto]`: una fila de un repetidor. Otra forma de dos niveles no se admite.
    if (!/^\d+$/.test(primero) || segundo === '' || /^\d+$/.test(segundo)) continue
    const n = Number(primero)
    if (n >= MAXIMO_DE_FILAS) continue
    const deLaRaiz = filas.get(raiz) ?? new Map<number, Record<string, unknown>>()
    const fila = deLaRaiz.get(n) ?? {}
    fila[segundo] = Array.isArray(valor) ? valor.at(-1) : valor
    deLaRaiz.set(n, fila)
    filas.set(raiz, deLaRaiz)
  }
  for (const [raiz, deLaRaiz] of filas) {
    salida[raiz] = [...deLaRaiz.entries()].sort(([a], [b]) => a - b).map(([, fila]) => fila)
  }
  for (const [clave, valor] of Object.entries(salida)) {
    if (Array.isArray(valor) && valor.some((v) => v === undefined)) salida[clave] = valor.filter((v) => v !== undefined)
  }
  return salida
}
